/**
 * 自动刷新 Hook
 * 提供可配置的自动刷新功能
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export interface AutoRefreshConfig {
  enabled?: boolean;
  interval?: number; // 刷新间隔（毫秒）
  onRefresh?: () => void | Promise<void>;
  manualRefreshCooldown?: number; // 手动刷新冷却时间（毫秒），默认10000
}

export interface AutoRefreshReturn {
  isRefreshing: boolean;
  lastRefreshTime: Date | null;
  nextRefreshIn: number; // 距离下次刷新的秒数
  manualRefresh: () => Promise<void>;
  toggleAutoRefresh: () => void;
  isAutoRefreshEnabled: boolean;
  cooldownRemaining: number; // 剩余冷却时间（秒）
  canManualRefresh: boolean; // 是否可以手动刷新
}

export const useAutoRefresh = ({
  enabled = false,
  interval = 30000, // 默认30秒
  onRefresh,
  manualRefreshCooldown = 10000 // 默认10秒冷却
}: AutoRefreshConfig): AutoRefreshReturn => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(0);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(enabled);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastManualRefreshRef = useRef<number>(0);

  // 计算是否可以手动刷新
  const canManualRefresh = !isRefreshing && cooldownRemaining === 0;

  // 执行刷新
  const executeRefresh = useCallback(async () => {
    if (isRefreshing || !onRefresh) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('刷新失败:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, onRefresh]);

  // 手动刷新（带冷却时间）
  const manualRefresh = useCallback(async () => {
    // 检查冷却时间
    const now = Date.now();
    const timeSinceLastRefresh = now - lastManualRefreshRef.current;
    
    if (timeSinceLastRefresh < manualRefreshCooldown && lastManualRefreshRef.current > 0) {
      // 仍在冷却中，不执行刷新
      return;
    }

    // 记录本次刷新时间
    lastManualRefreshRef.current = now;
    
    // 开始冷却倒计时
    setCooldownRemaining(Math.ceil(manualRefreshCooldown / 1000));
    
    // 清除之前的冷却计时器
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    // 设置冷却倒计时
    cooldownTimerRef.current = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 执行刷新
    await executeRefresh();
    
    // 重置自动刷新计时器
    if (isAutoRefreshEnabled) {
      setNextRefreshIn(Math.floor(interval / 1000));
    }
  }, [executeRefresh, isAutoRefreshEnabled, interval, manualRefreshCooldown]);

  // 切换自动刷新
  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled(prev => !prev);
  }, []);

  // 倒计时更新
  useEffect(() => {
    if (!isAutoRefreshEnabled) {
      setNextRefreshIn(0);
      return;
    }

    setNextRefreshIn(Math.floor(interval / 1000));

    countdownRef.current = setInterval(() => {
      setNextRefreshIn(prev => {
        if (prev <= 1) {
          return Math.floor(interval / 1000);
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [isAutoRefreshEnabled, interval]);

  // 自动刷新定时器
  useEffect(() => {
    if (!isAutoRefreshEnabled || !onRefresh) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // 立即执行一次刷新
    executeRefresh();

    // 设置定时刷新
    timerRef.current = setInterval(() => {
      executeRefresh();
    }, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAutoRefreshEnabled, interval, onRefresh, executeRefresh]);

  // 清理冷却计时器
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  return {
    isRefreshing,
    lastRefreshTime,
    nextRefreshIn,
    manualRefresh,
    toggleAutoRefresh,
    isAutoRefreshEnabled,
    cooldownRemaining,
    canManualRefresh
  };
};
